import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigationMode } from '@app/context/NavigationModeContext'

import { useNavigate } from '@lib/react-router-dom'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'
import { TotalPortfolioModal } from '@pages/Traders-Room/Wallets/modals/TotalPortfolio.modal'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { Caption2, Title1, Title2 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WalletIcon } from '@ui/icons/Wallet.icon'
import { Skeleton } from '@ui/v2/Skeleton'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { TotalWalletBalanceDto } from '@model/TotalWalletBalanceDto'

import { useDisableMainScroll } from '@hooks/useDisableMainScroll'
import { useFundingNav } from '@hooks/useFundingNav'

import { TMEntityLogoFactory } from '@assets/logo/TickmillLogoIcons'

import { AnimatedNumber } from '@shared/animations/AnimatedNumber'
import { useAccountSwitch } from '@shared/components/Navigation/ProductSwitch/useAccountSwitch'
import { useProductSwitch } from '@shared/components/Navigation/ProductSwitch/useProductSwitch'
import { useHideSensitiveData } from '@shared/components/Navigation/menuSections/topLevelMenus/useHideSensitiveData'
import {
  ACCOUNT_SWITCH_MENU_ID,
  useAccountSwitchMenu,
} from '@shared/components/Navigation/menuSections/useAccountSwitchMenu'
import {
  PRODUCT_SWITCH_MENU_ID,
  useProductSwitchMenu,
} from '@shared/components/Navigation/menuSections/useProductSwitchMenu'

import { useDepositTracker } from '@analytics/amplitude/deposit'

import { MenuLink } from '../../MenuLink'
import { AccountProductSwitch } from '../../ProductSwitch/AccountProductSwitch'
import { MenuSection } from '../../menuSections/useMenuSections'
import { isMenuItemActive } from '../Sidebar/utils'
import { useSidebarMenuSections } from '../hooks/useSidebarMenuSections'
import { MobileTab, useMobileBottomTabs } from './useMobileBottomTabs'
import { useSegmentedMenuSwitcher } from './useSegmentedMenuSwitcher'

import styles from './MobileBottomBar.module.scss'

export const MobileBottomBar: React.FC = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const tabs = useMobileBottomTabs()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { disableMainScroll, enableMainScroll } = useDisableMainScroll()

  const handleTabClick = useCallback(
    (tab: MobileTab) => {
      if (tab.isMenu) {
        setIsMenuOpen(true)
        disableMainScroll()
        return
      }
      if (tab.path) {
        navigate(tab.path)
      }
    },
    [navigate, disableMainScroll]
  )

  const isTabActive = useCallback(
    (tab: MobileTab) => {
      if (tab.isMenu) return false
      return tab.path ? pathname.includes(tab.path) : false
    },
    [pathname]
  )

  return (
    <>
      <nav className={styles.bottomBar} data-tour-target='wallets.nav'>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type='button'
            className={classNames(styles.tab, { [styles.active]: isTabActive(tab) })}
            onClick={() => handleTabClick(tab)}
            aria-label={tab.label}
            data-tour-target={tab.id === 'markets' ? 'navigation.trade' : undefined}
          >
            <div className={styles.tabIconWrapper}>
              <tab.icon size={24} color='currentColor' />
            </div>
            <Caption2 semibold className={styles.tabLabel}>
              {tab.label}
            </Caption2>
          </button>
        ))}
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <MobileMenuOverlay
            onClose={() => {
              enableMainScroll()
              setIsMenuOpen(false)
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

interface MobileMenuOverlayProps {
  onClose: () => void
}

const segmentedMenuList = {
  product: [PRODUCT_SWITCH_MENU_ID.CFD, PRODUCT_SWITCH_MENU_ID.ETD],
  account: [ACCOUNT_SWITCH_MENU_ID.PERSONAL, ACCOUNT_SWITCH_MENU_ID.PARTNER],
}

const MobileMenuOverlay: React.FC<MobileMenuOverlayProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const apiClient = useApiClient(ClientApiClient)
  const totalPortfolioValueModal = useModal(TotalPortfolioModal)
  const tracker = useDepositTracker()

  const navigate = useNavigate()
  const { navigateToFunding } = useFundingNav()
  const { isIBVisible } = useIBRoutesConditions()
  const { pathname } = useLocation()
  const { sidebarSections } = useSidebarMenuSections()
  const { navigationMode } = useNavigationMode()
  const hideSensitiveData = useHideSensitiveData()

  const [sectionStack, setSectionStack] = useState<string[]>([])
  const handleProductSwitch = useProductSwitch(onClose)
  const handleAccountSwitch = useAccountSwitch(onClose)
  const productSwitchMenu = useProductSwitchMenu(handleProductSwitch)
  const accountSwitchMenu = useAccountSwitchMenu(handleAccountSwitch)

  const sectionMap = useMemo(() => {
    const map = new Map<string, MenuSection>()
    const buildMap = (sections: MenuSection[]) => {
      sections.forEach((section: MenuSection) => {
        map.set(section.id, section)
        if (section.children?.length) {
          buildMap(section.children)
        }
      })
    }
    buildMap(sidebarSections)
    buildMap([productSwitchMenu, accountSwitchMenu])
    return map
  }, [sidebarSections, productSwitchMenu, accountSwitchMenu])

  const activeSection = useMemo(() => {
    if (sectionStack.length === 0) return null
    return sectionMap.get(sectionStack[sectionStack.length - 1]) || null
  }, [sectionStack, sectionMap])

  const entity = useSessionEntity()

  const activeSectionChildren = useMemo(
    () => activeSection?.children?.filter((child) => child.condition !== false) ?? [],
    [activeSection]
  )

  useEffect(() => {
    const SWAP_THRESHOLD = 50

    let touchStartX = 0,
      touchEndX = 0

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.changedTouches[0].screenX
    }
    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX = e.changedTouches[0].screenX
      if (touchEndX - touchStartX > SWAP_THRESHOLD) {
        onClose()
      }
    }
    window.addEventListener('touchstart', handleTouchStart, { capture: true })
    window.addEventListener('touchend', handleTouchEnd, { capture: true })

    return () => {
      window.removeEventListener('touchstart', handleTouchStart, { capture: true })
      window.removeEventListener('touchend', handleTouchEnd, { capture: true })
    }
  }, [onClose])

  const isSegmentedMenu = useMemo(
    () =>
      activeSectionChildren?.every(
        (s) => segmentedMenuList.account.includes(s.id) || segmentedMenuList.product.includes(s.id)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeSectionChildren]
  )

  const handleItemClick = useCallback(
    (item: MenuSection) => {
      if (item.children?.length) {
        setSectionStack((prev) => [...prev, item.id]) // push deeper
        return
      }
      if (item.onClick) {
        item.onClick()
        if (isSegmentedMenu || item.closeOnSelect) {
          onClose()
        }
        return
      }
      if (item.path) {
        navigate(item.path)
        onClose()
      }
    },
    [navigate, onClose, isSegmentedMenu]
  )

  const { activeSegmentedMenuTab, segmentedMenuSections } = useSegmentedMenuSwitcher(
    activeSectionChildren,
    isSegmentedMenu,
    handleItemClick
  )

  const tradingAccountTotals = useQuery({
    queryKey: queryKeys.tradingAccount.totals(),
    queryFn: () => apiClient.getTradingAccountsTotals(),
    refetchOnMount: 'always',
  })

  const totalWalletBalance = useQuery({
    queryKey: queryKeys.walletExtended.totalBalance(account?.displayCurrency?.id ?? 'USD'),
    queryFn: (): Promise<TotalWalletBalanceDto | null> =>
      apiClient.getTotalBalanceByCurrencyBothProducts(account?.displayCurrency?.id ?? 'USD'),
    refetchOnMount: 'always',
  })
  const totalBalanceValue =
    (totalWalletBalance.data?.balance || 0) +
    (tradingAccountTotals.data?.totalBalanceInDisplayCurrency || 0)
  const isTotalBalanceReady = totalWalletBalance.isFetched && tradingAccountTotals.isFetched
  const showProductSwitcher = useMemo(() => {
    return (
      entity === TickmillCompaniesEnum.TICKMILL_UK &&
      !!account?.visibilityConfiguration?.showProductSwitcher
    )
  }, [entity, account?.visibilityConfiguration?.showProductSwitcher])

  const showAccountSwitcher = isIBVisible

  const handleSectionClick = useCallback(
    (section: MenuSection) => {
      if (section.children?.length) {
        setSectionStack((prev) => [...prev, section.id])
        return
      }
      if (section.onClick) {
        section.onClick()
        onClose()
        return
      }
      if (section.path) {
        navigate(section.path)
        onClose()
      }
    },
    [navigate, onClose]
  )

  const handleBack = useCallback(() => {
    setSectionStack((prev) => prev.slice(0, -1))
  }, [])

  const isChildView = sectionStack.length > 0

  return (
    <motion.div
      className={styles.menuOverlay}
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'tween', duration: 0.25 }}
    >
      <div className={styles.menuHeader}>
        <AnimatePresence mode='wait'>
          {isChildView ? (
            <motion.div
              key='child-header'
              className={styles.menuHeaderInner}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <button
                className={styles.menuCloseButton}
                type='button'
                onClick={handleBack}
                aria-label={t('Back')}
              >
                <ChevronIcon direction={'left'} size={24} color='currentColor' />
              </button>
              <Title1 semibold>{activeSection?.label}</Title1>
            </motion.div>
          ) : (
            <motion.div
              key='root-header'
              className={styles.menuHeaderInner}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <div className={styles.menuLogo}>
                <TMEntityLogoFactory />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type='button'
          className={styles.menuCloseButton}
          onClick={onClose}
          aria-label={t('Close')}
        >
          <CloseIcon size={24} color='currentColor' />
        </button>
      </div>

      {!isChildView && (
        <div className={styles.menuBalanceRow}>
          <div className={styles.menuBalanceRowLeft}>
            <WalletIcon color='currentColor' />
            {isTotalBalanceReady ? (
              <Title2 semibold>
                <Truncate>
                  <SensitiveData>
                    ≈
                    <AnimatedNumber
                      value={totalBalanceValue}
                      currency={account?.displayCurrency?.id}
                      isReady={isTotalBalanceReady}
                    />
                  </SensitiveData>
                </Truncate>
              </Title2>
            ) : (
              <Skeleton as='span' inverse width={132} height={20} variant='rounded' />
            )}
            <IconButton.Ghost
              icon={{ icon: InfoIcon, color: 'text-white' }}
              onClick={() => totalPortfolioValueModal.open()}
            />
          </div>
          {hideSensitiveData.icon && (
            <IconButton.Ghost
              icon={{ icon: hideSensitiveData.icon, color: 'text-white' }}
              size='md'
              onClick={hideSensitiveData.onClick}
              aria-label={hideSensitiveData.label}
            />
          )}
        </div>
      )}

      <div className={styles.menuContentWrapper}>
        <AnimatePresence initial={false} mode='wait'>
          {!isChildView ? (
            <motion.div
              key='root'
              className={styles.menuContent}
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{ type: 'tween', duration: 0.05 }}
            >
              {sidebarSections.map((section) => {
                const isActive = isMenuItemActive(pathname, section.path)
                const hasChildren = !!section.children?.length

                return (
                  <MenuLink
                    key={section.id}
                    section={section}
                    isActive={isActive}
                    chevronDirection='right'
                    showChevron={hasChildren}
                    onClick={() => handleSectionClick(section)}
                    className={styles.menuItem}
                  />
                )
              })}
            </motion.div>
          ) : (
            <motion.div
              key={`children-${sectionStack.join('-')}`}
              className={styles.menuContent}
              initial={{ x: '100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '100%', opacity: 0 }}
              transition={{ type: 'tween', duration: 0.22 }}
            >
              {activeSectionChildren.length > 0 &&
                (isSegmentedMenu ? (
                  <AccountProductSwitch
                    activeTabId={activeSegmentedMenuTab?.id}
                    tabs={segmentedMenuSections}
                    isProductSwitch={showProductSwitcher}
                    isAccountSwitch={showAccountSwitcher}
                  />
                ) : (
                  activeSectionChildren.map((child) => (
                    <MenuLink
                      key={child.id}
                      section={child}
                      isActive={child.path ? pathname.startsWith(child.path) : false}
                      onClick={() => handleItemClick(child)}
                      className={styles.menuItem}
                    />
                  ))
                ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className={styles.menuFooter}>
        {activeSection === null && navigationMode !== 'partner' && (
          <Button.Tertiary
            fullWidth
            onClick={() => {
              tracker.start({ entry_point: 'add_deposit_shortcut' })
              navigateToFunding(CommonPaths.Deposit)
              onClose()
            }}
          >
            {t('Add Deposit')}
          </Button.Tertiary>
        )}
        {showProductSwitcher && sectionStack.length === 0 && (
          <MenuLink
            section={productSwitchMenu}
            chevronDirection='right'
            showChevron
            onClick={() => {
              handleSectionClick(productSwitchMenu)
            }}
            className={styles.menuItem}
          />
        )}
        {showAccountSwitcher && sectionStack.length === 0 && (
          <MenuLink
            section={accountSwitchMenu}
            chevronDirection='right'
            showChevron
            onClick={() => {
              handleSectionClick(accountSwitchMenu)
            }}
            className={styles.menuItem}
          />
        )}
      </div>
    </motion.div>
  )
}
